import * as R from 'ramda';
import {merge, of} from 'rxjs';
import {catchError, map, scan, startWith} from 'rxjs/operators';

const batchConcurrency = 3;

/** Run a list of observable operations concurrently
 Params:

 ops :: [{operation :: Observable, ...data :: any}]
 A list of operation objects with an observable operation and
 some metadata that will be emitted with the resulting observable
 as properties of the objects in the successful and failed arrays.

 Returns:
 An observable that emits status objects in the following format:
  {
    complete: boolean indicating completion of all requests
    success: boolean indicating success of all requests
    progress: total number of operations done, either succesfully or not
    total: total number of operations
    successful: [{...data: {succesfull operation data}, result: result data}, {more results...}],
    failed: [{...data: {failed operation data}, result: result data}, {more results...}]
  }

  Example:
  runBatchOps([{operation: op1, someId: 1}, {operation: op2, someId: 2}])
    .subscribe(results => {
      const succesfulIds = results.successful.map(obj => obj.someId).join(", ");
      console.log("These ops have now ran succesfully "+ succesfulIds);
    });

  */
export function runBatchOps(operationDescriptors) {
  const ops = operationDescriptors.map(({operation, ...data}) =>
    operation.pipe(
      map(result => ({...data, success: true, result})),
      catchError(result => of({...data, success: false, result})),
    ));

  function addStatusBits(results) {
    const {successful, failed, total} = results;
    return R.mergeRight(results, {
      progress: successful.length + failed.length,
      complete: successful.length + failed.length === total,
      success: successful.length === total
    });
  }

  return merge(...ops, batchConcurrency).pipe(
    startWith({
      total: ops.length,
      successful: [],
      failed: [],
    }),
    scan((results, result) => {
      return result.success ?
        R.evolve({successful: R.append(result)}, results) :
        R.evolve({failed:     R.append(result)}, results);
    }),
    map(addStatusBits)
  );
}



// WEBPACK FOOTER //
// ./modules/issFrontend/utils/batchOps.jsx